import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const articleLinks = [
  { label: 'Complete Guide 2026', slug: 'hipobuy-spreadsheet-guide' },
  { label: 'Full Review & Analysis', slug: 'hipobuy-spreadsheet-review' },
  { label: 'Kakobuy Comparison', slug: 'hipobuy-vs-kakobuy-spreadsheet' },
  { label: 'Safety & Privacy', slug: 'hipobuy-spreadsheet-safety' },
  { label: 'Beginner Tutorial', slug: 'hipobuy-spreadsheet-beginners' },
  { label: 'Tips & Tricks', slug: 'hipobuy-spreadsheet-tips' },
];

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategoryClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl font-bold whitespace-nowrap ${
              isScrolled ? 'text-foreground-900' : 'text-background-50'
            }`}
          >
            Hipobuy Sheet
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium whitespace-nowrap transition-colors ${
                isScrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'
              }`}
            >
              Home
            </Link>
            <Link
              to="/hipobuy-spreadsheet-guide"
              className={`text-sm font-medium whitespace-nowrap transition-colors ${
                isScrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'
              }`}
            >
              Guides
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center space-x-1 text-sm font-medium whitespace-nowrap transition-colors ${
                  isScrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'
                }`}
              >
                <span>Categories</span>
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-background-50 rounded-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryClick(cat.url)}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors flex items-center space-x-3 whitespace-nowrap"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      <span>{cat.name}</span>
                      {cat.hot && (
                        <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                          HOT
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/hipobuy-vs-kakobuy-spreadsheet"
              className={`text-sm font-medium whitespace-nowrap transition-colors ${
                isScrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/90 hover:text-background-50'
              }`}
            >
              Compare
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-medium px-5 py-2 rounded-full border transition-all whitespace-nowrap ${
                isScrolled
                  ? 'border-foreground-300 text-foreground-700 hover:border-foreground-500 hover:text-foreground-950'
                  : 'border-background-50/40 text-background-50 hover:border-background-50 hover:bg-background-50/10'
              }`}
            >
              Visit Store
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center ${
              isScrolled ? 'text-foreground-900' : 'text-background-50'
            }`}
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="px-4 py-3 space-y-4">
            <div>
              <div className="flex items-center space-x-2 px-3 mb-2">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-compass-3-line text-sm"></i>
                </span>
                <h3 className="text-xs font-bold text-foreground-400 uppercase tracking-widest">
                  Navigation
                </h3>
              </div>
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center space-x-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                  <i className="ri-home-line text-sm"></i>
                </span>
                <span className="font-medium">Home</span>
              </Link>
              <Link
                to="/hipobuy-spreadsheet-guide"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center space-x-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                  <i className="ri-book-open-line text-sm"></i>
                </span>
                <span className="font-medium">Complete Guide</span>
              </Link>
            </div>

            <div>
              <div className="flex items-center space-x-2 px-3 mb-2">
                <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                  <i className="ri-grid-line text-sm"></i>
                </span>
                <h3 className="text-xs font-bold text-foreground-400 uppercase tracking-widest">
                  14 Product Categories
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="flex items-center space-x-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors text-left whitespace-nowrap"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    <span className="font-medium text-xs">{cat.name}</span>
                    {cat.hot && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap leading-none">
                        HOT
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center space-x-2 px-3 mb-2">
                <span className="w-5 h-5 flex items-center justify-center text-secondary-500">
                  <i className="ri-article-line text-sm"></i>
                </span>
                <h3 className="text-xs font-bold text-foreground-400 uppercase tracking-widest">
                  SEO Resources
                </h3>
              </div>
              {articleLinks.map((article) => (
                <Link
                  key={article.slug}
                  to={`/${article.slug}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center space-x-3 px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-secondary-500">
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </span>
                  <span className="font-medium text-xs">{article.label}</span>
                </Link>
              ))}
            </div>

            <div className="pt-2 pb-1">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-center px-5 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors"
              >
                <span className="flex items-center justify-center space-x-2">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-shopping-bag-line text-sm"></i>
                  </span>
                  <span>Visit Hipobuy Sheet</span>
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line text-sm"></i>
                  </span>
                </span>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}